import React, { useState, useEffect } from 'react';
import './InstructionsModal.css';

/**
 * How to play.
 *
 * This used to open on every load and lead with a welcome screen carrying the
 * player's recent stats. The stats were the useful part and were being hidden
 * behind a wall, so they moved to StatsStrip; this modal is now the reference
 * material only, shown on a player's first visit and afterwards on demand via
 * the "?" in the strip.
 */
const InstructionsModal = ({ onClose }) => {
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={`modal-content ${isDesktop ? 'modal-content-wide' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-body">
          <div className="text-instructions">
            <h2 style={{ marginTop: 0, marginBottom: '0.5rem' }}>How to Play</h2>

            <p style={{ marginTop: 0 }}>
              JumbleCross is a crossword with a twist: you have the clues and you know the
              letters, but you don&apos;t know where each answer goes in the grid.
            </p>

            <h3 style={{ marginTop: '1.5rem', marginBottom: '0.5rem' }}>The Puzzle</h3>
            <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
              <li>Fill the crossword grid using the provided letter tiles</li>
              <li>Tiles can be single letters or multi-letter combinations</li>
              <li>Clues hint at answers hidden somewhere in the grid</li>
            </ul>

            {isDesktop ? (
              <div style={{ display: 'flex', gap: '2rem', marginTop: '1.5rem' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Keyboard Controls</h3>
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <strong>Navigation:</strong>
                      <ul style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                        <li><kbd>Arrow Keys</kbd> - Navigate between cells</li>
                        <li><kbd>Tab</kbd> / <kbd>Shift+Tab</kbd> - Switch to next/previous clue</li>
                        <li><kbd>Space</kbd> - Toggle direction (across ↔ down) when on an intersection</li>
                      </ul>
                    </div>

                    <div style={{ marginBottom: '0.75rem' }}>
                      <strong>Placing Tiles:</strong>
                      <ul style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                        <li><kbd>A-Z</kbd> - Type letters to place tiles</li>
                        <li>Type letter combinations quickly to place multi-letter tiles</li>
                      </ul>
                    </div>

                    <div style={{ marginBottom: '0.75rem' }}>
                      <strong>Removing Tiles:</strong>
                      <ul style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                        <li><kbd>Backspace</kbd> / <kbd>Delete</kbd> - Remove tile from current cell</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div style={{ flex: 1 }}>
                  <h3 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Mouse/Trackpad Controls</h3>
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <strong>Navigation:</strong>
                      <ul style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                        <li>Click a cell to select it; click again to toggle direction</li>
                        <li>Use the arrows beside the grid to navigate between clues</li>
                      </ul>
                    </div>

                    <div style={{ marginBottom: '0.75rem' }}>
                      <strong>Placing Tiles:</strong>
                      <ul style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                        <li>Click any tile in the letter bank to place it at the selected cell</li>
                        <li>Multi-letter tiles (double and triple) must match the selected direction</li>
                        <li>The cursor automatically moves to the next open cell after placing a tile</li>
                      </ul>
                    </div>

                    <div style={{ marginBottom: '0.75rem' }}>
                      <strong>Removing Tiles:</strong>
                      <ul style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                        <li>Double-click any tile in the grid to remove and return it to the letter bank</li>
                        <li>Use the undo button (↩️) in the controls to remove the last placed tile</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <h3 style={{ marginTop: '1.5rem', marginBottom: '0.5rem' }}>Controls</h3>
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <strong>Navigation:</strong>
                    <ul style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                      <li>Tap a cell to select it; tap again to toggle direction</li>
                      <li>Use the arrows beside the grid to navigate between clues</li>
                    </ul>
                  </div>

                  <div style={{ marginBottom: '0.75rem' }}>
                    <strong>Placing Tiles:</strong>
                    <ul style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                      <li>Tap any tile in the letter bank below the grid to place it at the selected cell</li>
                      <li>Multi-letter tiles (double and triple) must match the selected direction</li>
                    </ul>
                  </div>

                  <div style={{ marginBottom: '0.75rem' }}>
                    <strong>Removing Tiles:</strong>
                    <ul style={{ marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                      <li>Double-tap any placed tile in the grid to remove it and return it to the letter bank</li>
                      <li>Use the undo button (↩️) in the controls to remove the last placed tile</li>
                    </ul>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="navigation-buttons">
          <button onClick={onClose}>Get Started!</button>
        </div>
      </div>
    </div>
  );
};

export default InstructionsModal;
